import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useHttp } from "hooks/http.hook";
import { API_VERSION_V2 } from "endpointsConst";

import CompanyReview, { Review } from "./CompanyReview";
import ReviewsFilter, { ReviewFilter } from "./ReviewsFilter";
import styles from "./styles.module.scss";

const REVIEWS_LIMIT = 10;

interface CompanyReviewsProps {
    companyId: number | string | undefined;
}

function buildFilterParam(filter: ReviewFilter): string | undefined {
    if (filter === "positive") return "positive";
    if (filter === "negative") return "negative";
    return undefined;
}

export default function CompanyReviews({ companyId }: CompanyReviewsProps) {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [filter, setFilter] = useState<ReviewFilter>("all");
    const { request } = useHttp();
    const { t } = useTranslation();

    const observerRef = useRef<IntersectionObserver | null>(null);
    const offsetRef = useRef(0);
    const hasMoreRef = useRef(true);

    const fetchReviews = useCallback(
        (offset: number, append: boolean, currentFilter: ReviewFilter) => {
            if (!companyId) return;

            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const params = new URLSearchParams({
                limit: String(REVIEWS_LIMIT),
                offset: String(offset),
            });

            const filterParam = buildFilterParam(currentFilter);
            if (filterParam) {
                params.set("filter", filterParam);
            }

            request(`${API_VERSION_V2}/companies/${companyId}/reviews?${params}`)
                .then((res: { data: { count: number; items: Review[] } }) => {
                    const { count: totalCount, items } = res.data;
                    setCount(totalCount);

                    if (append) {
                        setReviews((prev) => [...prev, ...items]);
                    } else {
                        setReviews(items);
                    }

                    offsetRef.current = offset + items.length;
                    hasMoreRef.current = offset + items.length < totalCount;
                })
                .catch(() => {})
                .finally(() => {
                    setLoading(false);
                    setLoadingMore(false);
                });
        },
        [companyId, request],
    );

    useEffect(() => {
        if (!companyId) return;
        offsetRef.current = 0;
        hasMoreRef.current = true;
        setReviews([]);
        fetchReviews(0, false, filter);
    }, [companyId, fetchReviews, filter]);

    const loadMore = useCallback(() => {
        if (!hasMoreRef.current || loadingMore || loading) return;
        fetchReviews(offsetRef.current, true, filter);
    }, [fetchReviews, loadingMore, loading, filter]);

    const loadMoreRef = useRef(loadMore);
    loadMoreRef.current = loadMore;

    const sentinelRef = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();

        if (!node) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreRef.current();
                }
            },
            { rootMargin: "200px", threshold: 0 },
        );

        observerRef.current.observe(node);
    }, []);

    const handleFilterChange = useCallback((newFilter: ReviewFilter) => {
        setFilter(newFilter);
    }, []);

    const header = (
        <div className={styles.header}>
            <h3 className={styles.header__title}>
                {t("reviews.heading")}
                {!loading && ` (${count})`}
            </h3>
            <ReviewsFilter value={filter} onChange={handleFilterChange} />
        </div>
    );

    if (loading && reviews.length === 0) {
        return (
            <>
                {header}
                <div className={styles.list}>
                    <div className={styles.sentinel}>
                        <span className={styles.loadingText}>
                            {t("common.loading", "Загрузка...")}
                        </span>
                    </div>
                </div>
            </>
        );
    }

    if (!loading && reviews.length === 0) {
        return (
            <>
                {header}
                <div className={styles.empty}>
                    {filter === "all" ? t("reviews.empty") : t("reviews.emptyFiltered")}
                </div>
            </>
        );
    }

    return (
        <>
            {header}
            <div className={styles.list}>
                {reviews.map((review, index) => (
                    <CompanyReview key={index} review={review} />
                ))}
                <div ref={sentinelRef} className={styles.sentinel}>
                    {loadingMore && (
                        <span className={styles.loadingText}>
                            {t("common.loading", "Загрузка...")}
                        </span>
                    )}
                </div>
            </div>
        </>
    );
}
